import { router } from "@inertiajs/react"
import { type ReactNode } from "react"
import { CheckCircle2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  ModerationDecidedItem,
  ModerationQueueItem,
  PaginationMeta,
} from "@/lib/types"

interface Props {
  tab: string
  items: ModerationQueueItem[] | ModerationDecidedItem[]
  meta?: PaginationMeta
  urls: { index: string }
}

function statusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    pending:       "secondary",
    needs_changes: "outline",
    approved:      "default",
    rejected:      "destructive",
  }
  return <Badge variant={variants[status] ?? "outline"}>{status.replace("_", " ")}</Badge>
}

function typeBadge(type: string) {
  return (
    <Badge variant="outline" className="text-xs font-mono">
      {type === "Poi" ? "POI" : "Event"}
    </Badge>
  )
}

function QueueTable({ items, urls }: { items: ModerationQueueItem[]; urls: { index: string } }) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
        <CheckCircle2 className="mb-4 h-10 w-10 text-muted-foreground opacity-30" aria-hidden="true" />
        <p className="text-sm font-medium text-muted-foreground">Queue is empty.</p>
        <p className="mt-1 text-xs text-muted-foreground">
          All content has been reviewed — nice work.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Submitted by</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Submitted</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={item.review_id}
              className="cursor-pointer hover:bg-muted/50 focus-within:bg-muted/50"
              onClick={() => item.url && router.visit(item.url)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") item.url && router.visit(item.url) }}
              tabIndex={0}
              role="link"
              aria-label={`Review ${item.name}`}
            >
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell>{typeBadge(item.subject_type)}</TableCell>
              <TableCell className="text-muted-foreground">{item.region ?? "—"}</TableCell>
              <TableCell className="text-muted-foreground">{item.submitted_by ?? "—"}</TableCell>
              <TableCell>{statusBadge(item.status)}</TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(item.submitted_at).toLocaleDateString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function DecidedTable({ items, meta, urls }: { items: ModerationDecidedItem[]; meta?: PaginationMeta; urls: { index: string } }) {
  return (
    <>
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead>Decided by</TableHead>
              <TableHead>Decided</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No recent decisions.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow
                  key={item.review_id}
                  className="cursor-pointer hover:bg-muted/50 focus-within:bg-muted/50"
                  onClick={() => item.url && router.visit(item.url)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") item.url && router.visit(item.url) }}
                  tabIndex={0}
                  role="link"
                  aria-label={`View decision for ${item.name ?? "item"}`}
                >
                  <TableCell className="font-medium">{item.name ?? "—"}</TableCell>
                  <TableCell>{typeBadge(item.subject_type)}</TableCell>
                  <TableCell className="text-muted-foreground">{item.region ?? "—"}</TableCell>
                  <TableCell>{statusBadge(item.decision)}</TableCell>
                  <TableCell className="text-muted-foreground">{item.decided_by ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(item.decided_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {meta && meta.pages > 1 && (
        <div className="flex items-center justify-between py-3 text-sm text-muted-foreground">
          <span>{meta.count} items</span>
          <div className="flex gap-2">
            {meta.page > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  router.get(
                    urls.index,
                    { tab: "recently_decided", page: meta.page - 1 },
                    { preserveState: true }
                  )
                }
              >
                Previous
              </Button>
            )}
            <span className="flex items-center px-2">
              Page {meta.page} of {meta.pages}
            </span>
            {meta.page < meta.pages && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  router.get(
                    urls.index,
                    { tab: "recently_decided", page: meta.page + 1 },
                    { preserveState: true }
                  )
                }
              >
                Next
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default function ModerationIndex({ tab, items, meta, urls }: Props) {
  function switchTab(value: string) {
    router.get(urls.index, { tab: value }, { preserveState: false })
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Moderation Queue</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Review public POI and event submissions before they go live.
        </p>
      </div>

      <Tabs value={tab} onValueChange={switchTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="needs_changes">Needs Changes</TabsTrigger>
          <TabsTrigger value="recently_decided">Recently Decided</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <QueueTable items={items as ModerationQueueItem[]} urls={urls} />
        </TabsContent>

        <TabsContent value="needs_changes">
          <QueueTable items={items as ModerationQueueItem[]} urls={urls} />
        </TabsContent>

        <TabsContent value="recently_decided">
          <DecidedTable items={items as ModerationDecidedItem[]} meta={meta} urls={urls} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

ModerationIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
